import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Ban } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "../../../components/ui/Modal";
import { useCreateUser } from "../../../hooks/useUsers";
import { ApiError } from "../../../lib/api";
import { roleApi, bizRoleApi } from "../../../lib/roleApi";
import { useAuth } from "../../../hooks/useAuth";

// Departments
const DEPARTMENTS = [
  "Engineering",
  "HR",
  "Finance",
  "Operations",
  "Marketing",
  "IT",
  "Admin",
  "Health & Beauty",
] as const;

function generateUsername(first: string, last: string): string {
  const clean = (s: string) =>
    s.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
  const f = clean(first);
  const l = clean(last);
  if (!f && !l) return "";
  if (!l) return f;
  if (!f) return l;
  return `${f}.${l}`;
}

const schema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  email: z.string().email("Invalid email"),
  position: z.string().min(1, "Required"),
  department: z.string().min(1, "Required"),
  hireDate: z.string().min(1, "Required"),
  baseSalary: z.preprocess(
    (val) => (typeof val === "number" && isNaN(val) ? undefined : val),
    z.number().optional()
  ),
  roleId: z.string().optional(),
  excludeFromPayroll: z.boolean().default(false),
  excludeFromGovDeductions: z.boolean().default(false),
  workDays: z.array(z.string()).optional(),
  hoursPerDay: z.preprocess(
    (val) => (typeof val === "number" && isNaN(val) ? undefined : val),
    z.number().optional()
  ),
});

type FormValues = z.infer<typeof schema>;

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-[#dc2626] text-xs mt-1";

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Overrides the globally-active business (used by the Manage Users inline picker). */
  businessId?: string;
}

export function AddUserModal({ isOpen, onClose, onSuccess, businessId }: AddUserModalProps) {
  const createUser = useCreateUser();
  const { activeBusinessId } = useAuth();
  const scopedBusinessId = businessId ?? activeBusinessId ?? undefined;
  const isBizScope = !!scopedBusinessId;
  const { data: rolesData } = useQuery({
    queryKey: isBizScope ? ["biz-roles", scopedBusinessId] : ["admin-roles"],
    queryFn: () => isBizScope ? bizRoleApi.list(scopedBusinessId!) : roleApi.list(),
  });
  const roles = rolesData?.items ?? [];

  const {
    register,
    handleSubmit,
    setError,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { excludeFromPayroll: false },
  });

  const firstName = useWatch({ control, name: "firstName" }) ?? "";
  const lastName = useWatch({ control, name: "lastName" }) ?? "";

  const generatedUsername = generateUsername(firstName, lastName);

  const onSubmit = async (data: FormValues) => {
    try {
      const username = generateUsername(data.firstName, data.lastName);
      const workSchedule =
        data.workDays && data.workDays.length > 0 && data.hoursPerDay
          ? { days: data.workDays, hoursPerDay: data.hoursPerDay }
          : undefined;
      await createUser.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        username,
        position: data.position,
        department: data.department,
        hireDate: data.hireDate,
        baseSalary: data.baseSalary,
        roleId: data.roleId || undefined,
        excludeFromPayroll: data.excludeFromPayroll,
        excludeFromGovDeductions: data.excludeFromGovDeductions,
        workSchedule,
        businessId: scopedBusinessId,
      });
      reset();
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.code === "USERNAME_EXISTS") {
        setError("root", {
          message: `Username "${generateUsername(data.firstName, data.lastName)}" already exists. Try a different name combination.`,
        });
      } else {
        setError("root", { message: "Something went wrong. Please try again." });
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Employee" size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>First Name</label>
            <input {...register("firstName")} className={INPUT_CLASS} placeholder="Juan" autoFocus />
            {errors.firstName && <p className={ERROR_CLASS}>{errors.firstName.message}</p>}
          </div>
          <div>
            <label className={LABEL_CLASS}>Last Name</label>
            <input {...register("lastName")} className={INPUT_CLASS} placeholder="dela Cruz" />
            {errors.lastName && <p className={ERROR_CLASS}>{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Auto-generated username */}
        <div>
          <label className={LABEL_CLASS}>
            Username <span className="text-[#94a3b8] font-normal">(auto-generated)</span>
          </label>
          <input
            value={generatedUsername}
            readOnly
            className="w-full rounded-lg border border-[#e2e8f0] bg-[#f1f5f9] px-3 py-2 text-sm text-[#64748b] cursor-not-allowed select-all"
            placeholder="Will be generated from name…"
          />
          <p className="text-[10px] text-[#94a3b8] mt-1">
            Generated as firstname.lastname — used for sign-in
          </p>
        </div>

        {/* Email */}
        <div>
          <label className={LABEL_CLASS}>Email Address</label>
          <input {...register("email")} type="email" className={INPUT_CLASS} placeholder="juan@company.com" />
          {errors.email && <p className={ERROR_CLASS}>{errors.email.message}</p>}
        </div>

        {/* Position */}
        <div>
          <label className={LABEL_CLASS}>Position</label>
          <input {...register("position")} className={INPUT_CLASS} placeholder="e.g. Hair Stylist" />
          {errors.position && <p className={ERROR_CLASS}>{errors.position.message}</p>}
        </div>

        {/* Department */}
        <div>
          <label className={LABEL_CLASS}>Department</label>
          <select {...register("department")} className={INPUT_CLASS}>
            <option value="">Select department</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {errors.department && <p className={ERROR_CLASS}>{errors.department.message}</p>}
        </div>

        {/* Hire Date + Base Salary */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Hire Date</label>
            <input {...register("hireDate")} type="date" className={INPUT_CLASS} />
            {errors.hireDate && <p className={ERROR_CLASS}>{errors.hireDate.message}</p>}
          </div>
          <div>
            <label className={LABEL_CLASS}>Base Salary (₱) <span className="text-[#94a3b8] font-normal">(optional)</span></label>
            <input
              {...register("baseSalary", { valueAsNumber: true })}
              type="number" min="0" step="0.01"
              className={INPUT_CLASS} placeholder="25000"
            />
            {errors.baseSalary && <p className={ERROR_CLASS}>{errors.baseSalary.message}</p>}
          </div>
        </div>

        {/* Access Role */}
        <div>
          <label className={LABEL_CLASS}>
            {isBizScope ? "Business Access Role" : "Access Role"}{" "}
            <span className="text-[#94a3b8] font-normal">(optional)</span>
          </label>
          <select {...register("roleId")} className={INPUT_CLASS}>
            <option value="">— No role (basic employee access) —</option>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>
                {r.name}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-[#94a3b8] mt-1">
            {isBizScope
              ? "Controls which pages this employee can access within this business. Without a role, only standard employee pages are accessible."
              : "Controls which pages this employee can access. Without a role, only standard employee pages are accessible."}
          </p>
          {errors.roleId && <p className={ERROR_CLASS}>{errors.roleId.message}</p>}
        </div>

        {/* Exclude from payroll */}
        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
          <input
            type="checkbox"
            {...register("excludeFromPayroll")}
            className="mt-0.5 h-4 w-4 rounded border-[#e2e8f0] accent-[#dc2626]"
          />
          <div>
            <div className="flex items-center gap-1.5">
              <Ban className="h-3.5 w-3.5 text-[#dc2626]" />
              <span className="text-sm font-medium text-[#0f172a]">Exclude from payroll</span>
            </div>
            <p className="text-xs text-[#64748b] mt-0.5">
              This employee will be skipped during batch payroll processing (e.g. owner, admin-only accounts).
            </p>
          </div>
        </label>

        {/* Exclude from government deductions */}
        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
          <input
            type="checkbox"
            {...register("excludeFromGovDeductions")}
            className="mt-0.5 h-4 w-4 rounded border-[#e2e8f0] accent-[#d97706]"
          />
          <div>
            <div className="flex items-center gap-1.5">
              <Ban className="h-3.5 w-3.5 text-[#d97706]" />
              <span className="text-sm font-medium text-[#0f172a]">Exclude from government deductions</span>
            </div>
            <p className="text-xs text-[#64748b] mt-0.5">
              SSS, PhilHealth, and Pag-IBIG will not be deducted from this employee's payslip (e.g. contractual, project-based).
            </p>
          </div>
        </label>

        {/* Work Schedule */}
        <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-4 space-y-3">
          <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">
            Work Schedule <span className="text-[#94a3b8] normal-case font-normal">(optional)</span>
          </p>
          <div>
            <p className="text-xs font-medium text-[#374151] mb-2">Working Days</p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "monday", label: "Mon" },
                { value: "tuesday", label: "Tue" },
                { value: "wednesday", label: "Wed" },
                { value: "thursday", label: "Thu" },
                { value: "friday", label: "Fri" },
                { value: "saturday", label: "Sat" },
                { value: "sunday", label: "Sun" },
              ].map(({ value, label }) => (
                <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    value={value}
                    {...register("workDays")}
                    className="h-3.5 w-3.5 rounded border-[#e2e8f0] accent-[#2447d9]"
                  />
                  <span className="text-xs text-[#374151]">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>Expected Hours per Day</label>
            <input
              {...register("hoursPerDay", { valueAsNumber: true })}
              type="number"
              step="0.5"
              placeholder="e.g. 8"
              className="w-32 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
            />
            {errors.hoursPerDay && <p className={ERROR_CLASS}>{errors.hoursPerDay.message}</p>}
          </div>
        </div>

        {/* Root error */}
        {errors.root && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
            {errors.root.message}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !generatedUsername}
            className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Creating…" : "Create User & Send Invite"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
